import type { Database } from '#shared/types/database.types'
import { logApiError } from '~/utils/logger'

type WaitlistEntry = Database['public']['Tables']['vzw_waitlist_entries']['Row']

export interface WaitlistJoinInput {
  camp_id: string
  name: string
  email: string
  phone?: string
  requested_quantity: number
}

export interface WaitlistFilters {
  camp_id?: string
  status?: 'waiting' | 'notified' | 'converted' | 'expired' | 'cancelled'
  page?: number
  limit?: number
}

export const useWaitlist = () => {
  /**
   * Join a camp waitlist
   */
  const joinWaitlist = async (input: WaitlistJoinInput): Promise<WaitlistEntry> => {
    try {
      const response = await $api<{ data: WaitlistEntry }>(
        `/api/camps/${input.camp_id}/waitlist/join`,
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      )

      return response.data
    }
    catch (error) {
      logApiError(`/api/camps/${input.camp_id}/waitlist/join`, error, { input })
      throw error
    }
  }

  /**
   * Get waitlist entries for a camp
   */
  const getWaitlistEntries = async (campId: string, filters: Omit<WaitlistFilters, 'camp_id'> = {}): Promise<WaitlistEntry[]> => {
    try {
      const queryParams = new URLSearchParams()

      if (filters.status)
        queryParams.append('status', filters.status)
      if (filters.page)
        queryParams.append('page', filters.page.toString())
      if (filters.limit)
        queryParams.append('limit', filters.limit.toString())

      const queryString = queryParams.toString()
      const url = `/api/camps/${campId}/waitlist${queryString ? `?${queryString}` : ''}`

      const response = await $api<{ data: WaitlistEntry[] }>(url)

      return response.data
    }
    catch (error) {
      logApiError(`/api/camps/${campId}/waitlist`, error, { filters })
      throw error
    }
  }

  /**
   * Cancel a waitlist entry
   */
  const cancelWaitlistEntry = async (campId: string, entryId: string): Promise<WaitlistEntry> => {
    try {
      const response = await $api<{ data: WaitlistEntry }>(
        `/api/camps/${campId}/waitlist/${entryId}/cancel`,
        {
          method: 'POST',
        },
      )

      return response.data
    }
    catch (error) {
      logApiError(`/api/camps/${campId}/waitlist/${entryId}/cancel`, error)
      throw error
    }
  }

  /**
   * Notify all waiting users about ticket availability
   * (Called by admin when capacity increases)
   */
  const notifyWaitlist = async (campId: string): Promise<{ notified: number }> => {
    try {
      const response = await $api<{ data: { notified: number } }>(
        `/api/camps/${campId}/waitlist/notify-all`,
        {
          method: 'POST',
        },
      )

      return response.data
    }
    catch (error) {
      logApiError(`/api/camps/${campId}/waitlist/notify-all`, error)
      throw error
    }
  }

  /**
   * Get waitlist statistics for a camp
   */
  const getWaitlistStats = async (campId: string) => {
    try {
      const response = await $api<{
        data: {
          total: number
          waiting: number
          notified: number
          converted: number
        }
      }>(`/api/camps/${campId}/waitlist/stats`)

      return response.data
    }
    catch (error) {
      logApiError(`/api/camps/${campId}/waitlist/stats`, error)
      throw error
    }
  }

  return {
    joinWaitlist,
    getWaitlistEntries,
    cancelWaitlistEntry,
    notifyWaitlist,
    getWaitlistStats,
  }
}
